import { pluralizeWord } from "../../app-utils/string.js";
import {
  type AnalystSession,
  type CoordinateList,
  type Group,
  type Parent,
  type Project,
  type RegionFlag,
  type View,
} from "../../graphql.js";
import {
  getCoordListRoute,
  getFlagRoute,
  getGroupRoute,
  getParentRoute,
  getViewRoute,
} from "../../redux/modules/saved-view.js";
import { FEATURE_DISPLAY_NAME } from "../../settings/settings.js";
import { GenericTile, SimpleTile } from "../core/DashboardTiles.js";

interface GlobalTileProps<T> {
  className?: string;
  onClick?: any;
  data: T;
}

/* --- PROJECT --- */
export function ProjectTileMapper({ data, onClick, className }: GlobalTileProps<Project>) {
  return (
    <SimpleTile
      title={data.name}
      description={data.description}
      link={`/project/${data.id}`}
      className={className}
      time={data.lastModified}
      onClick={onClick}
      project={data}
    />
  );
}

/* --- VIEW --- */
export function ViewTileMapper({ data, onClick, className }: GlobalTileProps<View>) {
  return (
    <GenericTile
      title={data.name}
      description={data.description}
      key={data.id}
      link={getViewRoute(data)}
      type={data.viewLabel}
      onClick={onClick}
      className={className}
      project={data.project}
      timeDetails={{ creator: data.creator, creationDate: data.created }}
      private={data.shared === false}
    />
  );
}

/* --- GROUP --- */
export function GroupTileMapper({ data, onClick, className }: GlobalTileProps<Group>) {
  return (
    <GenericTile
      title={data.name}
      description={data.description}
      key={data.id}
      link={getGroupRoute(data)}
      type={`${data.itemCount} ${pluralizeWord(data.typeDisplayName, data.itemCount)}`}
      onClick={onClick}
      className={className}
      project={data.project}
      timeDetails={{ creator: data.creator, creationDate: data.created }}
      private={data.shared === false}
    />
  );
}

/* --- PARENT --- */
export function ParentTileMapper({ data, onClick, className }: GlobalTileProps<Parent>) {
  const description = (status: string, desc: string) => (
    <>
      {status ? <em>{status}</em> : null}
      {status && desc && " — "}
      {desc}
    </>
  );
  return (
    <GenericTile
      title={data.name}
      description={description(data.status && data.status.name, data.description)}
      key={data.id}
      link={getParentRoute(data)}
      type={`${data.sampleCount} ${pluralizeWord(data.typeDisplayName, data.sampleCount)}`}
      onClick={onClick}
      className={className}
      project={data.project}
      timeDetails={{ creator: data.creator, creationDate: data.created }}
      private={data.shared === false}
    />
  );
}

/* --- REGION FLAG --- */
export function RegionFlagTileMapper({ data, onClick, className }: GlobalTileProps<RegionFlag>) {
  return (
    <GenericTile
      title={data.name}
      description={data.description}
      key={data.id}
      link={getFlagRoute(data)}
      type={`${data.region.contig.organism.name}, ${data.region.contig.organism.species.name}`}
      onClick={onClick}
      className={className}
      project={data.project}
      timeDetails={{
        creator: data.creator,
        creationDate: data.created,
        modder: data.lastModifier,
        modificationDate: data.lastModified,
      }}
      private={data.shared === false}
    />
  );
}

/* --- ANALYST SESSION --- */
export function AnalystSessionTileMapper({ data, onClick, className }: GlobalTileProps<AnalystSession>) {
  return (
    <GenericTile
      title={data.name}
      description={data.description}
      key={data.id}
      type={FEATURE_DISPLAY_NAME[data.type]}
      onClick={() => onClick(data)}
      className={className}
      project={data.project}
      timeDetails={{ creator: data.creator, lastUseDate: data.modified }}
      private={data.shared === false}
    />
  );
}

/* --- COORDINATE LISTS --- */
export function CoordListTileMapper({ data, onClick, className }: GlobalTileProps<CoordinateList>) {
  return (
    <GenericTile
      title={data.name}
      description={data.description}
      key={data.id}
      link={getCoordListRoute(data)}
      type={`${data.count} ${pluralizeWord(data.typeDisplayName, data.count)}`}
      onClick={onClick}
      className={className}
      project={data.project}
      timeDetails={{
        creator: data.creator,
        creationDate: data.created,
        modificationDate: data.modified,
      }}
      private={data.shared === false}
    />
  );
}
